import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/config/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, UserMinus, UserPlus, Info, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SnitchReports = () => {
    const [reports, setReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchReports = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${API}/snitch/reports`);
            setReports(response.data);
        } catch (error) {
            console.error('Error fetching snitch reports:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'high': return 'text-red-400 bg-red-900/10 border-red-500/20';
            case 'medium': return 'text-amber-400 bg-amber-900/10 border-amber-500/20';
            case 'low': return 'text-blue-400 bg-blue-900/10 border-blue-500/20';
            default: return 'text-slate-400 bg-slate-800/20 border-slate-700/20';
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'slacker': return <UserMinus className="w-4 h-4" />;
            case 'suspicious': return <AlertTriangle className="w-4 h-4" />;
            case 'late': return <AlertTriangle className="w-4 h-4" />;
            default: return <Info className="w-4 h-4" />;
        }
    };

    return (
        <Card className="bg-slate-900 border-purple-500/30">
            <CardHeader className="flex flex-row items-center justify-between p-4 border-b border-purple-500/20 bg-gradient-to-r from-purple-900/10 to-transparent">
                <CardTitle className="text-purple-300 flex items-center gap-2 text-lg">
                    <AlertTriangle className="w-5 h-5" />
                    Le Soffiate di Andrea 🤫
                </CardTitle>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={fetchReports}
                    disabled={isLoading}
                    className="text-purple-400 hover:text-purple-300 hover:bg-purple-900/20 h-8 w-8"
                >
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
            </CardHeader>
            <CardContent className="p-4 space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar">
                {isLoading && reports.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-slate-500">
                        <RefreshCw className="w-8 h-8 animate-spin mb-2 opacity-20" />
                        <p className="text-sm italic">Andrea sta raccogliendo informazioni...</p>
                    </div>
                ) : reports.length === 0 ? (
                    <div className="text-center py-6 text-slate-500 italic text-sm">
                        Nessuna soffiata oggi. Tutti si comportano bene... per ora.
                    </div>
                ) : (
                    reports.map((report) => (
                        <div
                            key={report.id}
                            className={`p-3 rounded-lg border flex gap-3 animate-in slide-in-from-right-2 duration-300 ${getSeverityColor(report.severity)}`}
                        >
                            <div className="mt-0.5 opacity-70">
                                {getIcon(report.type)}
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-sm mb-1">{report.title}</h4>
                                <p className="text-xs leading-relaxed opacity-90">{report.content}</p>
                            </div>
                        </div>
                    ))
                )}

                <p className="text-[10px] text-slate-600 italic text-center pt-2">
                    * Le informazioni di Andrea potrebbero essere basate su pettegolezzi infondati o eccesso di zelo.
                </p>
            </CardContent>
        </Card>
    );
};

export default SnitchReports;
